<template>
  <div ref="root" class="terrain-date-range" :class="{ 'terrain-date-range--open': isOpen }">
    <span class="terrain-date-range__label">{{ label }}</span>
    <button
      ref="trigger"
      type="button"
      class="terrain-date-range__trigger terrain-focusable"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-haspopup="dialog"
      @click="isOpen = !isOpen"
    >
      <span class="terrain-date-range__calendar" aria-hidden="true">▣</span>
      <span>{{ rangeLabel }}</span>
      <span class="terrain-date-range__chevron" aria-hidden="true">▾</span>
    </button>

    <section v-if="isOpen" class="terrain-date-range__panel" role="dialog" :aria-label="`Rango de fechas: ${label}`">
      <div class="terrain-date-range__presets" aria-label="Rangos rápidos">
        <button type="button" @click="setToday">Hoy</button>
        <button type="button" @click="setNextWeek">Próximos 7 días</button>
        <button type="button" @click="setThisMonth">Este mes</button>
      </div>
      <div class="terrain-date-range__calendar-toolbar">
        <button type="button" class="terrain-date-range__month-nav" aria-label="Mes anterior" @click="moveMonth(-1)">‹</button>
        <p>{{ selectionHint }}</p>
        <button type="button" class="terrain-date-range__month-nav" aria-label="Mes siguiente" @click="moveMonth(1)">›</button>
      </div>
      <div class="terrain-date-range__calendars">
        <section v-for="month in calendarMonths" :key="month.key" class="terrain-date-range__month" :aria-label="month.label">
          <h3>{{ month.label }}</h3>
          <div class="terrain-date-range__weekdays" aria-hidden="true"><span>L</span><span>M</span><span>X</span><span>J</span><span>V</span><span>S</span><span>D</span></div>
          <div class="terrain-date-range__days">
            <button
              v-for="day in month.days"
              :key="day.value"
              type="button"
              class="terrain-date-range__day"
              :class="{ 'terrain-date-range__day--outside': !day.currentMonth, 'terrain-date-range__day--start': day.value === start, 'terrain-date-range__day--end': day.value === end, 'terrain-date-range__day--in-range': isInRange(day.value) }"
              :aria-label="day.ariaLabel"
              :aria-pressed="day.value === start || day.value === end"
              @click="selectDay(day.value)"
            >{{ day.day }}</button>
          </div>
        </section>
      </div>
      <div class="terrain-date-range__actions">
        <button type="button" class="terrain-date-range__clear" :disabled="!start && !end" @click="clear">Limpiar</button>
        <button type="button" class="terrain-date-range__apply" @click="close">Aplicar</button>
      </div>
    </section>
  </div>
</template>

<script>
function dateValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dateLabel(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })
    .format(new Date(`${value}T12:00:00`));
}

function addMonths(date, amount) { return new Date(date.getFullYear(), date.getMonth() + amount, 1); }

export default {
  name: 'TerrainDateRangePicker',
  props: {
    start: { type: String, default: '' },
    end: { type: String, default: '' },
    // label: por defecto conserva el texto de siempre (uso como filtro en
    // TerrainCampaigns.vue); TerrainCampaignEdit.vue lo reusa como el
    // propio rango de vigencia de la campaña con un label distinto.
    label: { type: String, default: 'Inicio de campaña' },
  },
  data() { return { isOpen: false, calendarMonth: new Date(new Date().getFullYear(), new Date().getMonth(), 1) }; },
  computed: {
    rangeLabel() {
      if (this.start && this.end) return `${dateLabel(this.start)} — ${dateLabel(this.end)}`;
      if (this.start) return `Desde ${dateLabel(this.start)}`;
      if (this.end) return `Hasta ${dateLabel(this.end)}`;
      return 'Seleccionar rango de fechas';
    },
    selectionHint() { return this.start && !this.end ? 'Selecciona la fecha de término' : 'Selecciona un rango'; },
    calendarMonths() { return [this.monthData(this.calendarMonth), this.monthData(addMonths(this.calendarMonth, 1))]; },
  },
  mounted() { document.addEventListener('mousedown', this.closeOnOutsideClick); },
  beforeDestroy() { document.removeEventListener('mousedown', this.closeOnOutsideClick); },
  methods: {
    close() { this.isOpen = false; },
    closeOnOutsideClick(event) {
      if (this.$refs.root && !this.$refs.root.contains(event.target)) this.close();
    },
    setRange(start, end) { this.$emit('update:start', start); this.$emit('update:end', end); },
    setToday() { const today = dateValue(new Date()); this.setRange(today, today); },
    setNextWeek() { const start = new Date(); const end = new Date(); end.setDate(end.getDate() + 6); this.setRange(dateValue(start), dateValue(end)); },
    setThisMonth() {
      const now = new Date();
      this.setRange(dateValue(new Date(now.getFullYear(), now.getMonth(), 1)), dateValue(new Date(now.getFullYear(), now.getMonth() + 1, 0)));
    },
    clear() { this.setRange('', ''); },
    moveMonth(amount) { this.calendarMonth = addMonths(this.calendarMonth, amount); },
    monthData(month) {
      const first = new Date(month.getFullYear(), month.getMonth(), 1);
      const gridStart = new Date(first); gridStart.setDate(first.getDate() - ((first.getDay() + 6) % 7));
      const formatter = new Intl.DateTimeFormat('es-CL', { month: 'long', year: 'numeric' });
      const dayFormatter = new Intl.DateTimeFormat('es-CL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      return {
        key: `${month.getFullYear()}-${month.getMonth()}`,
        label: formatter.format(month),
        days: Array.from({ length: 42 }, (_, index) => {
          const date = new Date(gridStart); date.setDate(gridStart.getDate() + index);
          return { value: dateValue(date), day: date.getDate(), currentMonth: date.getMonth() === month.getMonth(), ariaLabel: dayFormatter.format(date) };
        }),
      };
    },
    selectDay(value) {
      if (!this.start || this.end) { this.setRange(value, ''); return; }
      this.setRange(value < this.start ? value : this.start, value < this.start ? this.start : value);
    },
    isInRange(value) { return Boolean(this.start && this.end && value > this.start && value < this.end); },
  },
};
</script>

<style lang="scss" scoped>
.terrain-date-range { position: relative; min-width: 250px; }
.terrain-date-range__label { display: block; margin-bottom: var(--terrain-space-xs); color: var(--color-navy); font-size: var(--terrain-font-caption); font-weight: 600; }
.terrain-date-range__trigger {
  display: flex; align-items: center; width: 100%; min-height: 38px; gap: var(--terrain-space-sm); padding: var(--terrain-space-sm) var(--terrain-space-md);
  border: 1px solid var(--color-line); border-radius: var(--terrain-radius-md); background: var(--color-white); color: var(--color-graphite); font: inherit; text-align: left; cursor: pointer;
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);
  &:hover, &:focus { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); outline: none; }
}
.terrain-date-range__calendar { color: var(--color-steel); font-size: 1rem; }
.terrain-date-range__chevron { margin-left: auto; color: var(--color-ghost); font-size: .72rem; transition: transform var(--terrain-transition); }
.terrain-date-range--open .terrain-date-range__chevron { transform: rotate(180deg); }
.terrain-date-range__panel {
  /* #{"min"} escapa la función: libsass interpreta min() como la función de Sass y falla
     al recibir un calc(). Interpolado sale literal al CSS en ambas implementaciones. */
  position: absolute; z-index: 30; top: calc(100% + var(--terrain-space-xs)); right: 0; width: #{"min"}(420px, calc(100vw - 2 * var(--terrain-space-md)));
  display: grid; gap: var(--terrain-space-md); padding: var(--terrain-space-md); background: var(--color-white); opacity: 1;
  border: 1px solid var(--color-line); border-radius: var(--terrain-radius-lg); box-shadow: var(--terrain-shadow-md);
}
.terrain-date-range__presets, .terrain-date-range__actions { display: flex; flex-wrap: wrap; gap: var(--terrain-space-xs); }
.terrain-date-range__presets button, .terrain-date-range__clear, .terrain-date-range__apply { border: 1px solid var(--color-line); border-radius: var(--terrain-radius-sm); background: var(--color-white); color: var(--color-navy); font: inherit; padding: var(--terrain-space-xs) var(--terrain-space-sm); cursor: pointer; }
.terrain-date-range__presets button:hover, .terrain-date-range__clear:hover { background: var(--color-mist); }
.terrain-date-range__calendar-toolbar { display: grid; grid-template-columns: 32px 1fr 32px; align-items: center; text-align: center; }
.terrain-date-range__calendar-toolbar p { margin: 0; color: var(--color-navy); font-size: var(--terrain-font-caption); font-weight: 600; }
.terrain-date-range__month-nav { width: 32px; height: 32px; border: 0; border-radius: 50%; background: var(--color-white); color: var(--color-navy); font-size: 1.4rem; line-height: 1; cursor: pointer; }
.terrain-date-range__month-nav:hover { background: var(--color-mist); }
.terrain-date-range__calendars { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--terrain-space-lg); }
.terrain-date-range__month h3 { margin: 0 0 var(--terrain-space-sm); color: var(--color-navy); font-size: var(--terrain-font-caption); text-align: center; text-transform: capitalize; }
.terrain-date-range__weekdays, .terrain-date-range__days { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.terrain-date-range__weekdays { margin-bottom: var(--terrain-space-xs); color: var(--color-ghost); font-size: .68rem; font-weight: 700; text-align: center; }
.terrain-date-range__day { min-width: 0; height: 30px; border: 0; border-radius: var(--terrain-radius-sm); background: transparent; color: var(--color-graphite); font: inherit; font-size: .78rem; cursor: pointer; }
.terrain-date-range__day:hover { background: var(--color-mist); }
.terrain-date-range__day--outside { color: var(--color-ghost); opacity: .45; }
.terrain-date-range__day--in-range { border-radius: 0; background: var(--terrain-color-success-light); color: var(--color-navy); }
.terrain-date-range__day--start, .terrain-date-range__day--end { background: var(--color-steel); color: var(--color-white); font-weight: 700; }
.terrain-date-range__actions { justify-content: flex-end; }
.terrain-date-range__apply { border-color: var(--color-steel); background: var(--color-steel); color: var(--color-white); }
.terrain-date-range__clear:disabled { cursor: not-allowed; opacity: .55; }
@media (max-width: 620px) { .terrain-date-range { width: 100%; } .terrain-date-range__panel { left: 0; right: auto; } .terrain-date-range__calendars { grid-template-columns: 1fr; } .terrain-date-range__month:nth-child(2) { display: none; } }
</style>
